import type { ApiResponse } from '@creator/sdk/creator-sdk';
import { post as postApi } from '@creator/sdk/network/api-service';
import { getDisplayName, getUserId } from '@src/model/user/helpers';
import creatorSdk from '@src/services/creator-sdk';
import {
    type CollectionReference,
    collectionGroup,
    type DocumentSnapshot,
    getDocs,
    getFirestore,
    limit,
    type QueryConstraint,
    type QueryDocumentSnapshot,
    query,
    startAfter,
    where,
} from 'firebase/firestore/lite';
import { atom } from 'jotai';
import { atomFamily } from 'jotai/utils';
import { atomWithImmer } from 'jotai-immer';
import { getContentEntitiesAtom } from '../contentEntities/contentEntities';
import type { ContentEntity, ContentEntityType } from '../contentEntities/model';
import { jotaiStore } from '../store';
import { generateReactionId, getReactionById } from './helpers/reactions';

// Define the Reaction type
export interface Reaction {
    id: string;
    entityId: string;
    entityType: ContentEntityType;
    userId: string;
    username: string;
    profileName?: string;
    reactionName: string;
    timestamp: Date;
}

// Base atom for storing reactions
export const reactionsAtom = atomWithImmer<Record<string, Reaction>>({});
reactionsAtom.debugLabel = 'reactionsAtom';

export function setReaction(reaction: Reaction) {
    jotaiStore.set(reactionsAtom, draft => {
        draft[reaction.id] = reaction;
    });
}

export function setReactions(reactions: Record<string, Reaction>) {
    jotaiStore.set(reactionsAtom, draft => {
        Object.assign(draft, reactions);
    });
}

// --- Derived Atoms for Reading Reactions Data ---
export const reactionAtomFamily = atomFamily((reactionId: string | undefined) => {
    const derivedAtom = atom<Reaction | undefined>(get => (reactionId ? get(reactionsAtom)[reactionId] : undefined));
    derivedAtom.debugLabel = `reactionAtomFamily/${reactionId ?? 'undefined'}`;
    return derivedAtom;
});

export type LoadStatus = 'idle' | 'loading' | 'success' | 'error';

export interface ReactionCollectionCompositeKeyParams {
    type: 'entity' | 'user';
    entityType: ContentEntityType;
    entityId?: string;
    userId?: string;
    reactionName?: string;
}

export function generateReactionCollectionKey(params: ReactionCollectionCompositeKeyParams): string {
    const { type, entityType, entityId, reactionName, userId } = params;
    let key = type;
    if (entityType) key += `_entityType-${entityType}`;
    if (entityId) key += `_entityId-${entityId}`;
    if (userId) key += `_userId-${userId}`;
    if (reactionName) key += `_reactionName-${reactionName}`;
    return key;
}

interface ReactionCollectionState {
    ids: string[];
    lastDoc: QueryDocumentSnapshot<Reaction> | null;
    hasMore: boolean;
    status: LoadStatus;
    error: Error | null;
    fetchedEntityIds: string[];
}

export const reactionCollectionStateFamily = atomFamily(
    (params: ReactionCollectionCompositeKeyParams) => {
        const key = generateReactionCollectionKey(params);
        const baseAtom = atomWithImmer<ReactionCollectionState>({
            ids: [],
            fetchedEntityIds: [],
            lastDoc: null,
            hasMore: true,
            status: 'idle',
            error: null,
        });
        baseAtom.debugLabel = `reactionCollectionState_${key}`;
        return baseAtom;
    },
    (a, b) => generateReactionCollectionKey(a) === generateReactionCollectionKey(b)
);

// Helper functions for updating collection state
function addToReactionCollection(entityType: ContentEntityType, entityId: string, reactionId: string) {
    const collectionAtom = reactionCollectionStateFamily({
        type: 'user',
        entityType,
        entityId,
    });

    jotaiStore.set(collectionAtom, draft => {
        if (!draft.ids.includes(reactionId)) {
            draft.ids.push(reactionId);
        }
    });
}

function removeFromReactionCollection(entityType: ContentEntityType, entityId: string, reactionId: string) {
    const collectionAtom = reactionCollectionStateFamily({
        type: 'user',
        entityType,
        entityId,
    });

    jotaiStore.set(collectionAtom, draft => {
        draft.ids = draft.ids.filter(id => id !== reactionId);
    });
}

// API interaction
export interface ReactionApiRequestPayload {
    userId: string;
    entityId: string;
    entityType: ContentEntityType;
    reactionName: string;
}

export const postReaction = async (payload: ReactionApiRequestPayload) => {
    const { entityId, entityType, reactionName } = payload;

    // Optimistic update
    reactToContentEntity(entityType, entityId, reactionName);
    const authIdToken = await creatorSdk.accountModule.getIdToken();
    const { data } = await postApi<ApiResponse<ContentEntity['id']>>(
        '/post/reaction',
        payload,
        {
            Authorization: `Bearer ${authIdToken}`,
            'X-Firebase-AppCheck': '',
        },
        false
    );

    return data;
};

export const reactToContentEntity = (entityType: ContentEntityType, entityId: string, newReaction: string) => {
    const myUserId = getUserId();
    if (!myUserId) throw new Error('No user ID');

    const reactionId = generateReactionId(myUserId, entityType, entityId);
    const existing = getReactionById(reactionId);
    if (!existing) return addReactionToContentEntity(myUserId, entityType, entityId, newReaction); // Add a new reaction
    if (existing.reactionName === newReaction) return;
    removeReactionFromContentEntity(myUserId, entityType, entityId, existing.reactionName);
    addReactionToContentEntity(myUserId, entityType, entityId, newReaction); // Replace with the new one
};

const addReactionToContentEntity = (userId: string, entityType: ContentEntityType, entityId: string, reactionName: string) => {
    jotaiStore.set(getContentEntitiesAtom(entityType), draft => {
        const entity = draft[entityId];
        if (!entity) throw new Error(`ContentEntity ${entityType}/${entityId} not found`);
        entity.reactionsCount = entity.reactionsCount ?? {};
        entity.reactionsCount[reactionName] = (entity.reactionsCount[reactionName] ?? 0) + 1;
    });

    // Also add to reactions atom
    const reactionId = generateReactionId(userId, entityType, entityId);
    const newReaction: Reaction = {
        id: reactionId,
        entityId,
        entityType,
        userId,
        username: getDisplayName(),
        reactionName,
        timestamp: new Date(),
    };

    setReaction(newReaction);

    // Update collection if needed
    addToReactionCollection(entityType, entityId, reactionId);
};

const removeReactionFromContentEntity = (userId: string, entityType: ContentEntityType, entityId: string, reactionName: string) => {
    jotaiStore.set(getContentEntitiesAtom(entityType), draft => {
        const entity = draft[entityId];
        if (!entity) throw new Error(`ContentEntity ${entityType}/${entityId} not found`);

        entity.reactionsCount[reactionName] = (entity.reactionsCount[reactionName] ?? 1) - 1;
    });

    // Also remove from reactions atom
    const reactionId = generateReactionId(userId, entityType, entityId);

    jotaiStore.set(reactionsAtom, draft => {
        delete draft[reactionId];
    });

    // Update collection if needed
    removeFromReactionCollection(entityType, entityId, reactionId);
};

// Data fetching
export type LoadMoreReactionsParams = ReactionCollectionCompositeKeyParams & {
    fetchLimit?: number;
    entityIds?: string[];
};

interface FetchReactionsPayload {
    lowerBound?: DocumentSnapshot<Reaction> | null;
    fetchLimit?: number;
    queryConstraints?: QueryConstraint[];
    keyParams: ReactionCollectionCompositeKeyParams;
}

export const loadMoreReactionsByEntityIdsAtom = atom(null, async (get, set, params: LoadMoreReactionsParams) => {
    const { fetchLimit = 20, entityIds, ...keyParams } = params;

    const collectionAtom = reactionCollectionStateFamily(keyParams);
    const collectionKey = generateReactionCollectionKey(keyParams);
    const { fetchedEntityIds } = get(collectionAtom);

    const entityIdsToLoad = entityIds?.length ? entityIds.filter(id => !fetchedEntityIds.includes(id)) : [];
    if (!entityIdsToLoad.length) return; // Nothing to load

    set(collectionAtom, draft => {
        draft.status = 'loading';
        draft.error = null;
    });

    try {
        const queryConstraints = getQueryConstraints(keyParams, entityIdsToLoad);

        const { ids, data, hasMore } = await fetchReactions({
            fetchLimit,
            queryConstraints,
            keyParams,
        });

        // Update the reactions cache
        setReactions(data);

        // Update the collection state
        set(collectionAtom, draft => {
            const existingIds = new Set(draft.ids);
            const newIds = ids.filter(id => !existingIds.has(id));
            draft.ids.push(...newIds);
            draft.status = 'success';
            draft.error = null;

            // Keep track of which entity IDs have been loaded in this batch
            const existingFetchedEntityIds = new Set(draft.fetchedEntityIds);
            const newFetchedEntityIds = entityIdsToLoad.filter(id => !existingFetchedEntityIds.has(id));
            draft.fetchedEntityIds.push(...newFetchedEntityIds);
        });
    } catch (error) {
        console.error(`[loadMoreReactionsByEntityIdsAtom ${collectionKey}] Error:`, error);

        set(collectionAtom, draft => {
            draft.status = 'error';
            draft.error = error instanceof Error ? error : new Error(String(error) || 'Unknown error');
        });
    }
});

const getQueryConstraints = (params: ReactionCollectionCompositeKeyParams, entityIds?: string[]): QueryConstraint[] => {
    const { type, entityType, entityId, reactionName, userId } = params;
    const constraints: QueryConstraint[] = [where('entityType', '==', entityType)];

    switch (type) {
        case 'user': // My reactions
            if (!userId) throw new Error('userId is required for user reactions collection');
            if (!entityIds && !entityId) throw new Error('entityId is required when fetching my reactions');

            constraints.push(where('userId', '==', userId));
            if (entityId) constraints.push(where('entityId', '==', entityId));
            if (entityIds?.length) constraints.push(where('entityId', 'in', entityIds));
            if (reactionName) constraints.push(where('reactionName', '==', reactionName));
            break;

        case 'entity': {
            if (!entityId) throw new Error('entityId is required for entity reactions collection');
            constraints.push(where('entityId', '==', entityId));
            if (reactionName) constraints.push(where('reactionName', '==', reactionName));
            break;
        }

        default:
            ((_: never) => {
                throw new Error(`Unsupported reaction collection type: ${type}`);
            })(type);
    }

    return constraints;
};

async function fetchReactions(payload: FetchReactionsPayload) {
    const { lowerBound, fetchLimit = 20, queryConstraints = [], keyParams } = payload;
    const { entityType } = keyParams;
    const db = getFirestore(creatorSdk.getApp());
    const entitiesRef = collectionGroup(db, `${entityType}Reactions`) as CollectionReference<Reaction>;
    const paginationConstraints = lowerBound ? [startAfter(lowerBound)] : [];

    const reactionsQuery = query(entitiesRef, ...queryConstraints, ...paginationConstraints, limit(fetchLimit));
    const snapshot = await getDocs(reactionsQuery);
    const ids: string[] = [];
    const reactionsData: Record<string, Reaction> = {};
    for (const docSnap of snapshot.docs) {
        const data = docSnap.data();
        const { id, entityId, entityType, userId } = data;
        // Ensure ID is part of the object
        const _id = generateReactionId(userId, entityType, entityId);
        reactionsData[_id] = { ...data, id: _id };
        ids.push(_id);
    }

    const lastDoc = (snapshot.docs[snapshot.docs.length - 1] as QueryDocumentSnapshot<Reaction>) ?? null;
    const hasMore = snapshot.size === fetchLimit;

    return { ids, data: reactionsData, lastDoc, hasMore };
}
